import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Users, FolderKanban, ShieldCheck, Activity, UserPlus, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Trial", "Active", "Suspended", "Cancelled"];

const STATUS_STYLES = {
  Trial: "bg-amber-50 text-amber-700 border-amber-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Suspended: "bg-slate-100 text-slate-700 border-slate-300",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

function StatusPill({ value }) {
  return (
    <span
      data-testid={`workspace-status-${value}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[value] || "bg-slate-50 text-slate-600 border-slate-200"}`}
    >
      {value}
    </span>
  );
}

function WorkspaceForm({ initial, onSubmit, submitting }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    email_domain: initial?.email_domain || "",
    status: initial?.status || "Trial",
    primary_contact_name: initial?.primary_contact_name || "",
    primary_contact_email: initial?.primary_contact_email || "",
    company_admin_email: initial?.company_admin_email || "",
    notes: initial?.notes || "",
  }));

  return (
    <div className="space-y-3">
      <div>
        <Label>Company / Workspace name *</Label>
        <Input data-testid="workspace-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Email domain</Label>
          <Input data-testid="workspace-domain-input" placeholder="acmecivil.com.au" value={form.email_domain} onChange={(e) => setForm({ ...form, email_domain: e.target.value })} />
        </div>
        <div>
          <Label>Status</Label>
          <select data-testid="workspace-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Primary contact name</Label>
          <Input data-testid="workspace-contact-name-input" value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} />
        </div>
        <div>
          <Label>Primary contact email</Label>
          <Input data-testid="workspace-contact-email-input" type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} />
        </div>
      </div>
      <div>
        <Label>Company Admin email</Label>
        <Input data-testid="workspace-companyadmin-input" type="email" placeholder="admin@company.com.au" value={form.company_admin_email} onChange={(e) => setForm({ ...form, company_admin_email: e.target.value })} />
      </div>
      <div>
        <Label>Notes</Label>
        <textarea data-testid="workspace-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" />
      </div>
      <DialogFooter>
        <Button data-testid="workspace-form-submit" onClick={() => onSubmit(form)} disabled={submitting || !form.name.trim()} className="bg-teal-700 hover:bg-teal-800">
          {submitting ? "Saving…" : (initial?.id ? "Save changes" : "Create workspace")}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Companies() {
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // workspace doc when editing
  const [adminFor, setAdminFor] = useState(null); // workspace doc when assigning Company Admin

  const reload = async () => {
    setLoading(true);
    try {
      const [w, o] = await Promise.all([
        client.get("/platform/workspaces"),
        client.get("/platform/overview"),
      ]);
      setRows(w.data || []);
      setOverview(o.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workspaces");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleCreate = async (form) => {
    setSubmitting(true);
    try {
      await client.post("/platform/workspaces", form);
      toast.success("Workspace created.");
      setCreateOpen(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create workspace");
    } finally { setSubmitting(false); }
  };

  const handleUpdate = async (form) => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await client.patch(`/platform/workspaces/${editing.id}`, form);
      toast.success("Workspace updated.");
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update workspace");
    } finally { setSubmitting(false); }
  };

  const quickStatus = async (ws, status) => {
    try {
      await client.patch(`/platform/workspaces/${ws.id}`, { status });
      toast.success(`Marked ${ws.name} as ${status}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  return (
    <div className="px-4 py-8 sm:px-8" data-testid="platform-companies-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-teal-700">FlowQA Owner</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Companies</h1>
          <p className="mt-1 text-sm text-slate-600">All onboarded company workspaces on the FlowQA platform.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-workspace-btn" className="bg-teal-700 hover:bg-teal-800">
              <Plus className="mr-2 size-4" /> New workspace
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create company workspace</DialogTitle></DialogHeader>
            <WorkspaceForm onSubmit={handleCreate} submitting={submitting} />
          </DialogContent>
        </Dialog>
      </div>

      {overview && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <SummaryTile testId="overview-total-workspaces" icon={Building2} label="Workspaces" value={overview.workspace_count} />
          <SummaryTile testId="overview-active-workspaces" icon={ShieldCheck} label="Active" value={overview.active_workspace_count} accent="emerald" />
          <SummaryTile testId="overview-trial-workspaces" icon={Activity} label="Trial" value={overview.trial_workspace_count} accent="amber" />
          <SummaryTile testId="overview-total-users" icon={Users} label="Total users" value={overview.total_users} />
          <SummaryTile testId="overview-total-projects" icon={FolderKanban} label="Total projects" value={overview.total_projects} />
        </div>
      )}

      <Card className="mt-6 overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-4 py-3">Company / Workspace</th>
                <th className="px-4 py-3">Email domain</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Company Admin</th>
                <th className="px-4 py-3 text-right">Users</th>
                <th className="px-4 py-3 text-right">Projects</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500" data-testid="workspaces-loading">Loading workspaces…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500" data-testid="workspaces-empty">No workspaces yet. Click <strong>New workspace</strong> to add one.</td></tr>
              )}
              {!loading && rows.map((w) => (
                <tr key={w.id} data-testid={`workspace-row-${w.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{w.name}</div>
                    {w.primary_contact_name && (
                      <div className="text-xs text-slate-500">{w.primary_contact_name}{w.primary_contact_email ? ` · ${w.primary_contact_email}` : ""}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{w.email_domain || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3"><StatusPill value={w.status} /></td>
                  <td className="px-4 py-3 text-slate-700">{w.resolved_company_admin_email || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{w.user_count ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{w.project_count ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <select
                        data-testid={`workspace-quickstatus-${w.id}`}
                        value={w.status}
                        onChange={(e) => quickStatus(w, e.target.value)}
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <Button data-testid={`workspace-edit-${w.id}`} variant="outline" size="sm" onClick={() => setEditing(w)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button data-testid={`workspace-admin-${w.id}`} variant="outline" size="sm" onClick={() => setAdminFor(w)} title="Add / change Company Admin">
                        <UserPlus className="size-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit workspace</DialogTitle></DialogHeader>
          {editing && <WorkspaceForm initial={editing} onSubmit={handleUpdate} submitting={submitting} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!adminFor} onOpenChange={(o) => !o && setAdminFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Company Admin · {adminFor?.name}</DialogTitle>
          </DialogHeader>
          {adminFor && (
            <CompanyAdminForm
              workspace={adminFor}
              onDone={async () => { setAdminFor(null); await reload(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompanyAdminForm({ workspace, onDone }) {
  const [form, setForm] = useState({
    name: "",
    email: workspace.resolved_company_admin_email || "",
    phone: "",
    title: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const r = await client.post(`/platform/workspaces/${workspace.id}/admin`, payload);
      toast.success(`Company Admin set: ${r.data.email}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set Company Admin");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3" data-testid="company-admin-form">
      <p className="text-xs text-slate-500">
        Create or re-link the Company Admin for <b>{workspace.name}</b>. They will receive a FlowQA welcome email with their temporary password (Outbox + SMTP when configured).
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Full name *</Label>
          <Input data-testid="company-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label>Email *</Label>
          <Input data-testid="company-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={`admin@${workspace.email_domain || "company.com.au"}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Phone</Label>
          <Input data-testid="company-admin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <Label>Title / Role</Label>
          <Input data-testid="company-admin-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
      </div>
      <div>
        <Label>Temporary password (leave blank to auto-generate)</Label>
        <Input data-testid="company-admin-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto: Welcome@xxxxxxxx" />
        <p className="mt-1 text-[10px] text-slate-500">The admin will be prompted to change this on first sign in. Sent via FlowQA welcome email.</p>
      </div>
      <DialogFooter>
        <Button data-testid="company-admin-submit" onClick={submit} disabled={submitting || !form.name || !form.email} className="bg-teal-700 hover:bg-teal-800">
          {submitting ? "Saving…" : <><UserPlus className="mr-2 size-4" />Create / Link Company Admin</>}
        </Button>
      </DialogFooter>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, accent, testId }) {
  const accentClass = {
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
  }[accent] || "text-teal-700 bg-teal-50";
  return (
    <Card data-testid={testId} className="border-slate-200 p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-slate-500">{label}</div>
          <div className="mt-1 font-heading text-2xl font-black tracking-tighter text-slate-900">{value ?? 0}</div>
        </div>
        <div className={`grid size-8 place-items-center rounded-md ${accentClass}`}>
          <Icon className="size-4" />
        </div>
      </div>
    </Card>
  );
}
